import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import FieldSet from 'Components/FieldSet';
import Label from 'Components/Label';
import Button from 'Components/Link/Button';
import ConfirmModal from 'Components/Modal/ConfirmModal';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import PageToolbar from 'Components/Page/Toolbar/PageToolbar';
import PageToolbarButton from 'Components/Page/Toolbar/PageToolbarButton';
import PageToolbarSection from 'Components/Page/Toolbar/PageToolbarSection';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from '../components/TdarrTableRowCell';
import { align, icons, kinds } from 'Helpers/Props';
import { useTdarrNodes } from '../queries';
import { tdarrPost } from '../api';
import { TdarrNode } from '../types';
import ActivityActionMenu from '../components/ActivityActionMenu';

const columns = [
  { name: 'name', label: 'Node', isVisible: true, isSortable: false }, { name: 'status', label: 'Status', isVisible: true, isSortable: false },
  { name: 'engine', label: 'Engine', isVisible: true, isSortable: false }, { name: 'cpu', label: 'CPU', isVisible: true, isSortable: false },
  { name: 'memory', label: 'Memory', isVisible: true, isSortable: false }, { name: 'workers', label: 'Workers', isVisible: true, isSortable: false },
  { name: 'limits', label: 'Worker Limits', isVisible: true, isSortable: false }, { name: 'actions', label: 'Actions', isVisible: true, isSortable: false }
];

export default function Nodes() {
  const queryClient = useQueryClient();
  const nodes = useTdarrNodes();
  const items = Object.values(nodes.data ?? {});
  const [pendingId, setPendingId] = useState('');
  const [confirmAction, setConfirmAction] = useState<{ node: TdarrNode; action: 'restart' | 'disconnect' } | null>(null);
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
  const mutation = useMutation({
    mutationFn: ({ path, data }: { path: string; data: Record<string, unknown> }) => tdarrPost(path, { data }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['tdarr', 'nodes'] }); setMessage({ kind: 'success', text: 'Node action accepted by Tdarr.' }); },
    onError: () => setMessage({ kind: 'danger', text: 'Node action failed.' }),
    onSettled: () => setPendingId('')
  });
  const onAction = useCallback((node: TdarrNode, action: string) => {
    if (action === 'restart' || action === 'disconnect') { setConfirmAction({ node, action }); return; }
    setPendingId(node._id); setMessage(null);
    mutation.mutate({ path: '/update-node', data: { nodeID: node._id, nodeUpdates: { nodePaused: !node.nodePaused } } });
  }, [mutation]);
  const alterWorker = useCallback((node: TdarrNode, workerType: string, process: 'increase' | 'decrease') => {
    setPendingId(`${node._id}:${workerType}`); setMessage(null);
    mutation.mutate({ path: '/alter-worker-limit', data: { nodeID: node._id, process, workerType } });
  }, [mutation]);
  const runConfirmed = () => {
    if (!confirmAction) return;
    const { node, action } = confirmAction;
    setPendingId(node._id); setMessage(null); setConfirmAction(null);
    mutation.mutate({ path: action === 'restart' ? '/restart-node' : '/disconnect-node', data: { nodeID: node._id } });
  };
  return <PageContent title="Nodes">
    <PageToolbar><PageToolbarSection alignContent={align.LEFT}><PageToolbarButton label="Refresh" iconName={icons.REFRESH} isSpinning={nodes.isFetching} onPress={() => queryClient.invalidateQueries({ queryKey: ['tdarr', 'nodes'] })} /></PageToolbarSection></PageToolbar>
    <PageContentBody>
      <FieldSet legend="Nodes">
        {message && <Alert kind={message.kind}>{message.text}</Alert>}
        {nodes.isError && <Alert kind={kinds.DANGER}>Unable to load Tdarr nodes.</Alert>}
        {nodes.isSuccess && !items.length && <Alert>No Tdarr nodes are connected.</Alert>}
        {!!items.length && <Table columns={columns}><TableBody>{items.map((node) => {
          const limits = node.workerLimits ?? {};
          return <TableRow key={node._id}>
            <TableRowCell>{node.nodeName ?? node._id}</TableRowCell>
            <TableRowCell><Label kind={node.nodePaused ? kinds.WARNING : kinds.SUCCESS}>{node.nodePaused ? 'Paused' : 'Online'}</Label></TableRowCell>
            <TableRowCell>{node.nodeEngine ?? '—'}</TableRowCell>
            <TableRowCell>{node.resStats?.os?.cpuPerc ?? 0}%</TableRowCell>
            <TableRowCell>{node.resStats?.os?.memUsedGB ?? 0} / {node.resStats?.os?.memTotalGB ?? 0} GB</TableRowCell>
            <TableRowCell>{Object.keys(node.workers ?? {}).length}</TableRowCell>
            <TableRowCell>{(['healthcheckcpu', 'healthcheckgpu', 'transcodecpu', 'transcodegpu'] as const).map((workerType) => <div key={workerType}><span>{workerType === 'healthcheckcpu' ? 'Health CPU' : workerType === 'healthcheckgpu' ? 'Health GPU' : workerType === 'transcodecpu' ? 'Transcode CPU' : 'Transcode GPU'}: {limits[workerType] ?? 0}</span>{' '}<Button size="small" isDisabled={(limits[workerType] ?? 0) <= 0 || mutation.isPending} onPress={() => alterWorker(node, workerType, 'decrease')}>−</Button>{' '}<Button size="small" isDisabled={mutation.isPending} onPress={() => alterWorker(node, workerType, 'increase')}>+</Button></div>)}</TableRowCell>
            <TableRowCell><ActivityActionMenu actions={[{ key: 'pause', label: node.nodePaused ? 'Resume node' : 'Pause node' }, { key: 'restart', label: 'Restart node' }, { key: 'disconnect', label: 'Force disconnect' }]} isSpinning={pendingId === node._id} onAction={(action) => onAction(node, action)} /></TableRowCell>
          </TableRow>;
        })}</TableBody></Table>}
      </FieldSet>
      <ConfirmModal isOpen={Boolean(confirmAction)} kind="danger" title={confirmAction?.action === 'restart' ? 'Restart Node' : 'Force Disconnect Node'} message={confirmAction?.action === 'restart' ? `Restart ${confirmAction.node.nodeName ?? confirmAction.node._id}? Active workers may be interrupted.` : `Force ${confirmAction?.node.nodeName ?? confirmAction?.node._id} to disconnect? Active workers will be interrupted.`} confirmLabel={confirmAction?.action === 'restart' ? 'Restart' : 'Disconnect'} isSpinning={mutation.isPending} onConfirm={runConfirmed} onCancel={() => setConfirmAction(null)} />
    </PageContentBody>
  </PageContent>;
}
